<template>
  <div
    class="mascot-wrap"
    :class="[`mascot-${variant}`, { 'is-mobile': isMobile }]"
    :style="parallaxStyle"
  >
    <!-- Soft glow halo -->
    <div class="mascot-halo" :class="`halo-${variant}`"></div>

    <!-- Mascot image -->
    <img
      ref="mascotEl"
      :src="mascotSrc"
      alt="Tilly mascot"
      class="mascot-img"
      draggable="false"
    />

    <!-- Floating sparkles around mascot -->
    <span class="mascot-spark spark-1">✦</span>
    <span class="mascot-spark spark-2">✧</span>
    <span class="mascot-spark spark-3">✦</span>

    <!-- Optional state badge (e.g. phone icon when "calling") -->
    <div v-if="badge" class="mascot-badge">{{ badge }}</div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import mascotSrc from '@/assets/images/tilly-mascot.png';

export default {
  name: 'TillyMascot',
  props: {
    // idle | listening | calling | curating | celebrate
    variant: { type: String, default: 'idle' },
    badge: { type: String, default: '' },
    // follow cursor on desktop
    cursorTrack: { type: Boolean, default: false },
  },
  setup(props) {
    const mascotEl = ref(null);
    const tilt = ref({ x: 0, y: 0 });
    const isMobile = ref(false);

    const updateIsMobile = () => {
      if (typeof window === 'undefined') return;
      isMobile.value = window.matchMedia('(max-width: 767px)').matches;
    };

    const onPointerMove = (e) => {
      if (isMobile.value) return;
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const dx = (e.clientX - cx) / cx;
      const dy = (e.clientY - cy) / cy;
      // Clamp
      tilt.value = {
        x: Math.max(-1, Math.min(1, dx)) * 8,
        y: Math.max(-1, Math.min(1, dy)) * 8,
      };
    };

    const onOrient = (e) => {
      if (!isMobile.value) return;
      // gamma = left/right tilt, beta = front/back tilt
      const g = (e.gamma || 0) / 45;
      const b = (e.beta || 0) / 90;
      tilt.value = {
        x: Math.max(-1, Math.min(1, g)) * 6,
        y: Math.max(-1, Math.min(1, b)) * 6,
      };
    };

    const parallaxStyle = computed(() => ({
      '--tilt-x': `${tilt.value.x}px`,
      '--tilt-y': `${tilt.value.y}px`,
    }));

    onMounted(() => {
      updateIsMobile();
      window.addEventListener('resize', updateIsMobile);
      if (props.cursorTrack) {
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('deviceorientation', onOrient);
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateIsMobile);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('deviceorientation', onOrient);
    });

    return { mascotEl, mascotSrc, parallaxStyle, isMobile };
  },
};
</script>

<style scoped>
.mascot-wrap {
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 320px;
  aspect-ratio: 1 / 1;
  filter: drop-shadow(0 24px 48px rgba(88, 168, 225, 0.35));
  transform: translate3d(var(--tilt-x, 0), var(--tilt-y, 0), 0);
  transition: transform 0.4s cubic-bezier(0.22, 0.61, 0.36, 1);
  pointer-events: none;
  will-change: transform;
}

.mascot-img {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  /* Spring entrance + organic float (ternapp onboarding style) */
  animation: mascot-spring-in 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both,
    mascot-wiggle 0.64s ease-out 0.7s 1 both,
    mascot-float 5.2s cubic-bezier(0.45, 0.05, 0.55, 0.95) 1.4s infinite;
}

/* Spring scale-in (overshoots to 1.08, snaps back to 1) */
@keyframes mascot-spring-in {
  0% {
    transform: scale(0.3) translateY(20px);
    opacity: 0;
  }
  60% {
    transform: scale(1.08) translateY(-4px);
    opacity: 1;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* Settle wiggle: ±12° ±6° ±2° ±0° with shrinking durations */
@keyframes mascot-wiggle {
  0% {
    transform: rotate(0deg);
  }
  28% {
    transform: rotate(12deg);
  }
  56% {
    transform: rotate(-6deg);
  }
  78% {
    transform: rotate(2deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

/* Organic float — slow drift up, longer hover at peak, gentle drop, soft tilt + breath */
@keyframes mascot-float {
  0% {
    transform: translateY(0) rotate(0deg) scale(1);
  }
  18% {
    transform: translateY(-6px) rotate(-1.5deg) scale(1.005);
  }
  38% {
    transform: translateY(-11px) rotate(0.5deg) scale(1.012);
  }
  55% {
    transform: translateY(-9px) rotate(2deg) scale(1.008);
  }
  72% {
    transform: translateY(-3px) rotate(0.5deg) scale(1.002);
  }
  88% {
    transform: translateY(2px) rotate(-1deg) scale(0.998);
  }
  100% {
    transform: translateY(0) rotate(0deg) scale(1);
  }
}

/* Variant: celebrate — bigger jump with anticipation + squash/stretch */
.mascot-celebrate .mascot-img {
  animation: mascot-spring-in 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both,
    mascot-wiggle 0.64s ease-out 0.7s 1 both,
    mascot-jump 3.2s cubic-bezier(0.45, 0.05, 0.55, 0.95) 1.4s infinite;
}
@keyframes mascot-jump {
  0%,
  100% {
    transform: translateY(0) scaleY(1) scaleX(1);
  }
  10% {
    /* Anticipation squat */
    transform: translateY(3px) scaleY(0.94) scaleX(1.06);
  }
  30% {
    /* Up-stretch */
    transform: translateY(-22px) scaleY(1.06) scaleX(0.96) rotate(-3deg);
  }
  45% {
    transform: translateY(-26px) scaleY(1) scaleX(1) rotate(2deg);
  }
  60% {
    /* Land squash */
    transform: translateY(0) scaleY(0.92) scaleX(1.08);
  }
  72% {
    transform: translateY(-4px) scaleY(1.02) scaleX(0.99);
  }
  85% {
    transform: translateY(-1px) scaleY(1) scaleX(1);
  }
}

/* Halo */
.mascot-halo {
  position: absolute;
  inset: -10%;
  border-radius: 50%;
  z-index: 1;
  background: radial-gradient(
    circle,
    rgba(253, 173, 0, 0.35) 0%,
    rgba(88, 168, 225, 0.25) 35%,
    transparent 70%
  );
  filter: blur(40px);
  opacity: 0.85;
  animation: halo-pulse 6s ease-in-out infinite;
}
@keyframes halo-pulse {
  0%,
  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
}
.halo-listening {
  background: radial-gradient(
    circle,
    rgba(123, 184, 232, 0.5) 0%,
    rgba(88, 168, 225, 0.3) 35%,
    transparent 70%
  );
}
.halo-calling {
  background: radial-gradient(
    circle,
    rgba(244, 132, 108, 0.45) 0%,
    rgba(253, 173, 0, 0.25) 40%,
    transparent 70%
  );
}
.halo-curating {
  background: radial-gradient(
    circle,
    rgba(123, 184, 232, 0.4) 0%,
    rgba(244, 132, 108, 0.25) 40%,
    transparent 70%
  );
}
.halo-celebrate {
  background: radial-gradient(
    circle,
    rgba(253, 173, 0, 0.6) 0%,
    rgba(244, 132, 108, 0.3) 40%,
    transparent 70%
  );
  animation-duration: 2s;
}

/* Sparkles */
.mascot-spark {
  position: absolute;
  font-size: 1.5rem;
  color: #fdad00;
  text-shadow: 0 0 12px rgba(253, 173, 0, 0.7);
  z-index: 3;
  pointer-events: none;
  animation: spark-twinkle 3s ease-in-out infinite;
}
.spark-1 {
  top: 8%;
  right: 10%;
  animation-delay: 0s;
}
.spark-2 {
  bottom: 18%;
  left: 6%;
  color: #58a8e1;
  text-shadow: 0 0 12px rgba(88, 168, 225, 0.6);
  animation-delay: 1s;
  font-size: 1.1rem;
}
.spark-3 {
  top: 30%;
  left: -2%;
  color: #f4846c;
  text-shadow: 0 0 10px rgba(244, 132, 108, 0.6);
  animation-delay: 1.8s;
  font-size: 0.9rem;
}

@keyframes spark-twinkle {
  0%,
  100% {
    opacity: 0.2;
    transform: scale(0.7) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.2) rotate(180deg);
  }
}

/* Badge for state hints */
.mascot-badge {
  position: absolute;
  bottom: 6%;
  right: -6%;
  z-index: 4;
  background: white;
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: #1a1a1a;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
  white-space: nowrap;
  animation: badge-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes badge-pop {
  0% {
    opacity: 0;
    transform: scale(0.6) translateY(8px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .mascot-img,
  .mascot-halo,
  .mascot-spark {
    animation: none !important;
  }
  .mascot-wrap {
    transition: none;
  }
}
</style>
